// SignaturePaywall.jsx
import React, { useState } from "react";
import {
    Box, Card, CardContent, CardHeader, Typography, Button, Stack, Chip,
    List, ListItem, ListItemIcon, ListItemText, Divider, Alert, CircularProgress
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SecurityIcon from "@mui/icons-material/Security";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { useSnackbar } from "notistack";

import { api } from "../../../services/Main/Api";
import { getCondoId } from "../../../services/Main/AuthStorage";

const formatBRL = (value) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

/**
 * Paywall para ativar Assinaturas Digitais (Plano mensal)
 * - Faz POST /sign/activate/ com { condo_id, plan, quantity }
 * - Ao concluir chama onActivated()
 */
const SignaturePaywall = ({ onActivated }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const price = 49;
    const quota = 20;

    const handleActivate = async () => {
        try {
            setLoading(true);
            // 🔧 ajuste o endpoint/payload conforme seu backend
            await api.post("/company/connect-plugsign/", {
                company: getCondoId(),
                //plan: "monthly_20", // plano mensal de 20 assinaturas
                //quantity: quota,
            }).then(response => {
                const { data } = response;
                if (data.success) {
                    enqueueSnackbar(data.message, { variant: "success" });
                    onActivated?.();
                }else{
                    enqueueSnackbar(data.message, { variant: "error" });
                }

            })


        } catch (err) {
            console.error(err);
            enqueueSnackbar("Não foi possível ativar agora. Tente novamente.", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Hero */}
            <Box
                sx={{
                    borderRadius: 3,
                    p: { xs: 3, md: 6 },
                    mb: 4,
                    color: "common.white",
                    background:
                        "linear-gradient(135deg, #7C3AED 0%, #5B21B6 35%, #0EA5E9 100%)",
                }}
            >
                <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="center" justifyContent="space-between">
                    <Stack spacing={1}>
                        <Chip
                            label="Novo • Assinaturas Digitais"
                            size="small"
                            sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "white", width: "fit-content" }}
                        />
                        <Typography variant="h4" fontWeight={800} lineHeight={1.2}>
                            Destrave assinaturas em poucos cliques.
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 720 }}>
                            Envie contratos, colete assinaturas com validade jurídica e acompanhe tudo dentro do BlocoB3.

                        </Typography>
                    </Stack>
                    <RocketLaunchIcon sx={{ fontSize: 72, opacity: 0.9 }} />
                </Stack>
            </Box>

            {/* Pricing Card */}
            <Card elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <CardHeader
                    title="Plano Starter"
                    subheader="20 assinaturas mensais"
                    sx={{ pb: 0 }}
                    action={<Chip color="primary" icon={<WorkspacePremiumIcon />} label="Mais Popular" />}
                />
                <CardContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={4} alignItems="stretch">
                        {/* Price / CTA */}
                        <Card sx={{ flex: "0 0 360px", borderRadius: 2 }}>
                            <CardContent>
                                <Stack spacing={1}>
                                    <Stack direction="row" alignItems="baseline" spacing={1}>
                                        <Typography variant="h3" fontWeight={800}>{formatBRL(price)}</Typography>
                                        <Typography variant="body2" color="text.secondary">/ mês</Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        Inclui {quota} assinaturas todo mês
                                    </Typography>

                                    <List dense sx={{ mt: 1 }}>
                                        {[
                                            "20 assinaturas/mês (franquia renova a cada ciclo)",
                                            "Validade jurídica com trilha de auditoria",
                                            "Envio por e-mail e acompanhamento em tempo real",
                                            "Modelo de contrato via upload (Docx)",
                                            "Relatórios e status por signatário",
                                        ].map((txt) => (
                                            <ListItem key={txt} disableGutters>
                                                <ListItemIcon sx={{ minWidth: 28 }}>
                                                    <CheckCircleIcon fontSize="small" color="success" />
                                                </ListItemIcon>
                                                <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={txt} />
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={loading ? null : <LockOpenIcon />}
                                        onClick={handleActivate}
                                        disabled={loading}
                                        sx={{ mt: 1 }}
                                        fullWidth
                                    >
                                        {loading ? <CircularProgress size={22} /> : "Ativar assinaturas"}
                                    </Button>

                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                        <SecurityIcon fontSize="small" color="action" />
                                        <Typography variant="caption" color="text.secondary">
                                            Renovação automática. Você pode cancelar quando quiser.
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Highlights / FAQ */}
                        <Box sx={{ flex: 1 }}>
                            <Alert icon={<MonetizationOnIcon />} severity="info" sx={{ mb: 2 }}>
                                Pague apenas {formatBRL(price)} por mês e tenha direito a {quota} assinaturas mensais.
                            </Alert>

                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                Por que ativar agora?
                            </Typography>
                            <List dense>
                                {[
                                    "Economize tempo e reduza erros – tudo fica registrado.",
                                    "Fechamentos muito mais rápidos.",
                                    "Transmita profissionalismo aos clientes.",
                                    "Controle e relatórios integrados no BlocoB3.",
                                ].map((txt) => (
                                    <ListItem key={txt} disableGutters>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <CheckCircleIcon fontSize="small" color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={txt} />
                                    </ListItem>
                                ))}
                            </List>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" color="text.secondary">
                                Dúvidas rápidas
                            </Typography>
                            <List dense>
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary="Como funciona a franquia?"
                                        secondary="Todo mês você recebe 20 assinaturas disponíveis. Itens não utilizados não acumulam para o mês seguinte."
                                    />
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary="Posso cancelar?"
                                        secondary="Sim. O cancelamento é a qualquer momento e você mantém o acesso até o fim do período pago."
                                    />
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary="E se eu precisar de mais?"
                                        secondary="Você pode migrar de plano ou contratar volume adicional conforme sua necessidade."
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SignaturePaywall;
